import { identityRecipients } from "../../common/age-identity";
import { ALGORITHMS, keyLength } from "./algorithms";
import { boxKeypair, boxPublicKey } from "./box";
import { decodeBytes, encodeBytes, type Encoding } from "./encoding";
import type { Mode } from "./run";

// A shared link carries whatever somebody put in it, so every one of these lands on something usable rather than
// trusting it; the ciphers themselves are then only ever handed a setting this page would have offered.
export function pickAlgorithm(value: unknown): string {
  return typeof value === "string" && value in ALGORITHMS ? value : "aes-gcm";
}

export function pickMode(value: unknown): Mode {
  return value === "decrypt" ? "decrypt" : "encrypt";
}

export function pickEncoding(value: unknown, fallback: Encoding): Encoding {
  return value === "hex" || value === "base64" ? value : fallback;
}

export function pickKeySize(algorithm: string, value: unknown): number {
  const sizes = ALGORITHMS[algorithm].keySizes;
  if (!sizes) return 32;
  return typeof value === "number" && sizes.includes(value) ? value : sizes[sizes.length - 1];
}

export function pickRecipient(value: unknown): "key" | "password" {
  return value === "password" ? "password" : "key";
}

// On, where `age` itself needs the flag, because this page opens on the Text source and a message is what armour is
// for; a link that says nothing about it lands on the PEM every one of these pages wrote before there was a control.
export function pickArmor(value: unknown): boolean {
  return typeof value === "boolean" ? value : true;
}

export function pickText(value: unknown): string {
  return typeof value === "string" ? value : "";
}

export function randomKey(algorithm: string, keySize: number, encoding: Encoding): string {
  const family = ALGORITHMS[algorithm].family;
  const bytes = family === "box" ? boxKeypair().secretKey : randomBytes(keyLength(algorithm, keySize));
  return encodeBytes(bytes, encoding);
}

export function randomNonce(algorithm: string, encoding: Encoding): string {
  return encodeBytes(randomBytes(ALGORITHMS[algorithm].nonceBytes), encoding);
}

export function derivedPublicKey(secret: string, encoding: Encoding): string {
  try {
    return encodeBytes(boxPublicKey(decodeBytes(secret, encoding)), encoding);
  } catch {
    return "";
  }
}

// The same move the line under a box key makes: an identity that reads says which recipient it answers for, and one
// that does not says nothing at all, the failure belonging under the output where every other one on this page lands.
// Async where the box family's is not, because deriving one is Web Crypto's X25519 wherever a browser has it.
export async function derivedRecipients(identities: string): Promise<string[]> {
  try {
    return await identityRecipients(identities);
  } catch {
    return [];
  }
}

export function randomBytes(length: number): Uint8Array {
  return crypto.getRandomValues(new Uint8Array(length));
}

// A field that is blank has not been answered yet rather than answered wrongly, so it reads back as neither bytes nor
// an error and the page simply has nothing to show; anything else is judged the moment it is typed.
export function readField(text: string, encoding: Encoding, expected: number): Field {
  if (!text.trim()) return { bytes: null, error: null };
  let bytes: Uint8Array;
  try {
    bytes = decodeBytes(text, encoding);
  } catch (e) {
    return { bytes: null, error: e instanceof Error ? e.message : "Cannot be read" };
  }
  if (expected > 0 && bytes.length !== expected) {
    return { bytes: null, error: `Needs ${expected} bytes, and this is ${bytes.length}` };
  }
  return { bytes, error: null };
}

export interface Field {
  bytes: Uint8Array | null;
  error: string | null;
}

// Web Crypto answers a wrong key, a wrong nonce and an edited ciphertext with one bare OperationError carrying no
// message at all, and the AEADs throw over the tag; from outside they are the same failure and get the same sentence.
export function message(e: unknown, mode: Mode): string {
  if (e instanceof DOMException) {
    if (e.name !== "OperationError") return e.message || "That did not work";
    return mode === "decrypt" ? UNSEALED : "That did not encrypt — check the key and the nonce";
  }
  if (e instanceof Error) {
    return TAG_FAILURE.test(e.message) && mode === "decrypt" ? UNSEALED : e.message || "That did not work";
  }
  return "That did not work";
}

const UNSEALED = "That did not decrypt — the key, the nonce or the ciphertext is not the one it was sealed with";

const TAG_FAILURE = /invalid tag|poly1305|wrong tag|decryption failed/i;
